//@flow
import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {
  createHeaderBannerService,
  deleteHeaderBannerService,
  listHeaderBannersService,
  updateHeaderBannerService,
} from '../promises/header-banner';
import {actions} from '../duck';

import type {HeaderBannerRequest} from '../api/header-banner';
import type {HeaderBanner} from '../../../entities';
import type {RootState} from '../../';
import {useActions} from '../../../utils/globalStore';

const headerBannerSelector = (state: RootState) => state.homePage.headerBanners;

export function useInitializeHeaderBanner(): {
  loading: boolean,
  headerBanners: HeaderBanner[],
} {
  const headerBanners = useSelector(headerBannerSelector);

  const setHeaderBanner = useActions(actions.setSavedHeaderBanners);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    listHeaderBannersService()
      .then(banners => {
        setHeaderBanner(banners);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setHeaderBanner]);

  return {
    loading,
    headerBanners,
  };
}

export function useHeaderBanner() {
  const headerBanner = useSelector(headerBannerSelector);

  return headerBanner;
}

const headerBannerActions = {
  createBanner: actions.addHeaderBanner,
  deleteBanner: actions.deleteHeaderBanner,
  editBanner: actions.updateHeaderBanner,
  enabledBanner: actions.enableHeaderBanner,
};

export function useHeaderBannerActions() {
  const [loading, setLoading] = useState(false);

  const {createBanner, deleteBanner, editBanner, enabledBanner} = useActions(
    headerBannerActions
  );

  const createHeaderBanner = (data: HeaderBannerRequest) => {
    setLoading(true);
    return createHeaderBannerService(data)
      .then(banner => {
        createBanner(banner);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteHeaderBanner = (bannerId: string) => {
    setLoading(true);
    return deleteHeaderBannerService(bannerId)
      .then(() => {
        deleteBanner(bannerId);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const editHeaderBanner = (bannerId: string, data: HeaderBannerRequest) => {
    setLoading(true);
    return updateHeaderBannerService(bannerId, data)
      .then(banner => {
        editBanner(bannerId, banner);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const enabledHeaderBanner = (bannerId: string, enabled: boolean) => {
    setLoading(true);
    return updateHeaderBannerService(bannerId, {enabled})
      .then(banner => {
        enabledBanner(bannerId, enabled);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return {
    loading,
    createHeaderBanner,
    deleteHeaderBanner,
    editHeaderBanner,
    enabledHeaderBanner,
  };
}
